// src/admin/pages/car/BulkCarUpload.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Stack,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    Divider,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    Upload as UploadIcon,
    Download as DownloadIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    Info as InfoIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
    Category,
    Warehouse,
    ProductFormData,
} from '../../../enums/product';
import { CarModel } from '../carmodel/carModel';
import { ProductService } from '../car/carService';
import { CategoryService } from '../category/categoryService';
import { fetchActiveCarModels } from '../carmodel/carModelService';
import { WarehouseService } from '../branch/branchService';
import { Brand, fetchActiveBrands } from '../brand/brandService';
import { useAuth } from '../../../contexts/AuthContext';
import { useInvalidateProducts } from '../../../hooks/useCars';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

interface ParsedRow {
    rowIndex: number;     // 1-based (excluding header)
    raw: Record<string, string>;
}

type RowStatus = 'pending' | 'uploading' | 'success' | 'error';

interface ProcessedProduct {
    rowIndex: number;
    productName: string;
    sku: string;
    status: RowStatus;
    error?: string;
    formData?: ProductFormData;
}

interface UploadStats {
    total: number;
    success: number;
    failed: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// CSV COLUMN DEFINITIONS
// These are the exact header names expected in the CSV.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * COLUMN REFERENCE
 *
 * One row = one physical car. There is no quantity/stock column and no
 * variant columns — a car is a single VIN-identified unit, not a stackable
 * retail SKU (see answers.text).
 *
 * REQUIRED:
 *   car_title             - Display name, e.g. "2021 Toyota Corolla GLi"
 *   category_name         - Must match an existing category name (case-insensitive)
 *   price                 - Numeric price
 *   sku                   - VIN / Chassis number — unique per vehicle
 *   branch_name            - Must match an existing active branch (case-insensitive)
 *
 * OPTIONAL, BUT MUST MATCH THE DATABASE IF PROVIDED — a row is rejected
 * (not uploaded) if any of these are supplied but don't match an existing
 * record. Leave the cell blank to skip them entirely.
 *   brand_name             - Must match an existing brand name
 *   model_name             - Must match a model under brand_name
 *   trim_name              - Must match a trim under model_name
 *
 * OTHER OPTIONAL:
 *   description            - Plain text or HTML description
 *   discount               - Discount % (0-100), default 0
 *   unit_cost              - Purchase/landed cost
 *   is_featured            - "yes" / "no", default "no"
 *   model_year, registration_year, mileage_km, fuel_type, transmission,
 *   engine_cc, drive_type, seating_capacity, exterior_color, interior_color,
 *   condition, status (CarStatus, default "In Stock"), registration_number,
 *   owners_count, country_of_origin
 */

// ─────────────────────────────────────────────────────────────────────────────
// SAMPLE CSV GENERATOR
// One row = one physical car (one VIN, one branch). No quantity/variant
// columns — a car isn't a stackable SKU.
// ─────────────────────────────────────────────────────────────────────────────

function generateSampleCSV(
    categories: Category[],
    brands: Brand[],
    warehouses: Warehouse[],
    carModels: CarModel[]
): string {
    const catName = categories[0]?.name || 'Sedan';
    const brandName = brands[0]?.name || 'Toyota';
    const branch1 = warehouses[0]?.name || 'Main Branch';
    const model1 = carModels.find(m => m.brandId === brands[0]?.id)?.name || 'Corolla';
    const trim1 = carModels.find(m => m.brandId === brands[0]?.id)?.trims?.[0]?.name || '';

    const headers = [
        'car_title', 'description', 'category_name', 'brand_name', 'model_name', 'trim_name',
        'branch_name', 'price', 'discount', 'unit_cost', 'sku', 'is_featured',
        'model_year', 'registration_year', 'mileage_km', 'fuel_type', 'transmission',
        'engine_cc', 'drive_type', 'seating_capacity', 'exterior_color', 'interior_color',
        'condition', 'status', 'registration_number', 'owners_count', 'country_of_origin',
    ];

    const row1 = [
        '2021 Toyota Corolla GLi', 'Well-maintained, single owner.', catName, brandName, model1, trim1,
        branch1, '5500000', '0', '5000000', 'NRE210-1234567', 'no',
        '2021', '2021', '32000', 'Petrol', 'Automatic',
        '1800', 'FWD', '5', 'Pearl White', 'Black',
        'Used - Excellent', 'In Stock', '', '1', '',
    ];

    const row2 = [
        '2019 Honda Civic Turbo', 'Imported, full service history.', catName, 'Honda', 'Civic', '',
        branch1, '7200000', '5', '6500000', 'FC1-9876543', 'yes',
        '2019', '2020', '48000', 'Petrol', 'CVT',
        '1500', 'FWD', '5', 'Crystal Black', 'Grey',
        'Used - Good', 'In Stock', '', '2', 'Japan',
    ];

    const escape = (v: string) => {
        if (v.includes(',') || v.includes('"') || v.includes('\n')) {
            return `"${v.replace(/"/g, '""')}"`;
        }
        return v;
    };

    const rows = [headers, row1, row2]
        .map(r => r.map(escape).join(','))
        .join('\n');

    return rows;
}

// ─────────────────────────────────────────────────────────────────────────────
// CSV PARSER  (no external dependency)
// ─────────────────────────────────────────────────────────────────────────────

// Single pass, RFC-4180-style state machine — parses straight into rows of
// columns instead of splitting into lines first and re-splitting each line.
// The old two-pass version stripped quote characters while building lines
// (to decide where a *quoted* newline should not end a row), which meant
// the per-line column splitter that ran afterwards had no quote markers
// left to see — so every comma inside a quoted field (e.g. a description
// like "Well-maintained, single owner.") was treated as a column
// separator and silently shifted every later column over by one.
function parseCSV(text: string): { headers: string[]; rows: Record<string, string>[]; columnCounts: number[] } {
    const rowsRaw: string[][] = [];
    let row: string[] = [];
    let col = '';
    let inQuote = false;
    let sawAnyField = false;

    const endCol = () => { row.push(col.trim()); col = ''; };
    const endRow = () => {
        endCol();
        rowsRaw.push(row);
        row = [];
        sawAnyField = false;
    };

    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch === '"') {
            if (inQuote && text[i + 1] === '"') { col += '"'; i++; }
            else { inQuote = !inQuote; }
            sawAnyField = true;
        } else if (ch === ',' && !inQuote) {
            endCol();
            sawAnyField = true;
        } else if ((ch === '\n' || ch === '\r') && !inQuote) {
            // Treat \r\n as one line break; ignore a lone \r followed by \n.
            if (ch === '\r' && text[i + 1] === '\n') continue;
            if (sawAnyField || col.length > 0) endRow();
        } else {
            col += ch;
            sawAnyField = true;
        }
    }
    if (sawAnyField || col.length > 0) endRow();

    if (rowsRaw.length < 2) return { headers: [], rows: [], columnCounts: [] };

    const headers = rowsRaw[0].map(h => h.toLowerCase().trim());
    const rows: Record<string, string>[] = [];
    const columnCounts: number[] = [];

    for (let i = 1; i < rowsRaw.length; i++) {
        const cols = rowsRaw[i];
        if (cols.length === 1 && cols[0] === '') continue;
        const rowObj: Record<string, string> = {};
        headers.forEach((h, idx) => { rowObj[h] = (cols[idx] ?? '').trim(); });
        rows.push(rowObj);
        columnCounts.push(cols.length);
    }

    return { headers, rows, columnCounts };
}

// ─────────────────────────────────────────────────────────────────────────────
// ROW → ProductFormData TRANSFORMER
//
// Brand / model / trim are optional, but if a name IS supplied it must match
// an existing database record exactly (case-insensitive) — otherwise the row
// is rejected outright (status 'error') and never uploaded. This is stricter
// than category/branch only in name; those two were always required and
// always hard-failed on mismatch, so brand/model/trim now behave the same
// way instead of silently uploading with the reference dropped.
// ─────────────────────────────────────────────────────────────────────────────

interface TransformContext {
    categories: Category[];
    brands: Brand[];
    warehouses: Warehouse[];
    carModels: CarModel[];
}

function bool(v: string): boolean {
    return v.toLowerCase() === 'yes' || v === '1' || v.toLowerCase() === 'true';
}

function resolveCategory(
    name: string,
    ctx: TransformContext
): { category: Category | null; error?: string } {
    if (!name.trim()) return { category: null, error: 'category_name is required' };
    const found = ctx.categories.find(c => c.name.toLowerCase() === name.toLowerCase());
    if (!found) return { category: null, error: `Category "${name}" not found` };
    return { category: found };
}

function resolveBrand(
    name: string,
    ctx: TransformContext
): { brand: Brand | null; error?: string } {
    if (!name.trim()) return { brand: null };
    const found = ctx.brands.find(b => b.name.toLowerCase() === name.toLowerCase());
    if (!found) return { brand: null, error: `Brand "${name}" does not exist in the database` };
    return { brand: found };
}

function resolveBranch(
    name: string,
    ctx: TransformContext
): { branch: Warehouse | null; error?: string } {
    if (!name.trim()) return { branch: null, error: 'branch_name is required — every car belongs to exactly one branch' };
    const found = ctx.warehouses.find(w => w.isActive && w.name.toLowerCase() === name.toLowerCase());
    if (!found) return { branch: null, error: `Active branch "${name}" not found` };
    return { branch: found };
}

function resolveModel(
    name: string,
    brandId: string,
    ctx: TransformContext
): { model: CarModel | null; error?: string } {
    if (!name.trim()) return { model: null };
    const found = ctx.carModels.find(
        m => m.brandId === brandId && m.name.toLowerCase() === name.toLowerCase()
    );
    if (!found) return { model: null, error: `Model "${name}" does not exist in the database for the selected brand` };
    return { model: found };
}

function resolveTrim(
    name: string,
    model: CarModel | null
): { trimId: string; error?: string } {
    if (!name.trim() || !model) return { trimId: '' };
    const found = model.trims.find(t => t.name.toLowerCase() === name.toLowerCase());
    if (!found) return { trimId: '', error: `Trim "${name}" does not exist in the database for model "${model.name}"` };
    return { trimId: found.id || '' };
}

// ─────────────────────────────────────────────────────────────────────────────
// GROUP ROWS BY car_title  (preserving order — one row = one car, but grouping
// is kept in case a future column needs multi-row support)
// ─────────────────────────────────────────────────────────────────────────────

interface ProductGroup {
    productName: string;
    rows: Record<string, string>[];
    firstRowIndex: number; // 1-based
}

function groupRows(parsedRows: ParsedRow[]): ProductGroup[] {
    const map = new Map<string, ProductGroup>();
    const order: string[] = [];

    for (const { rowIndex, raw } of parsedRows) {
        const name = (raw['car_title'] || '').trim();
        if (!name) continue;
        if (!map.has(name)) {
            map.set(name, { productName: name, rows: [], firstRowIndex: rowIndex });
            order.push(name);
        }
        map.get(name)!.rows.push(raw);
    }

    return order.map(n => map.get(n)!);
}

// ─────────────────────────────────────────────────────────────────────────────
// TRANSFORM: one ProductGroup → ProcessedProduct
// ─────────────────────────────────────────────────────────────────────────────

function transformGroup(
    group: ProductGroup,
    ctx: TransformContext
): ProcessedProduct {
    const errors: string[] = [];
    const first = group.rows[0];

    if (first['__columnMismatch']) errors.push(first['__columnMismatch']);

    // ── Core fields ──
    const productName = group.productName;
    const description = first['description'] || '';
    const isFeatured = bool(first['is_featured'] || 'no');
    const price = first['price'] || '';
    const sku = first['sku'] || '';
    const discount = first['discount'] || '0';
    const unitCost = first['unit_cost'] || '0';

    if (!price || parseFloat(price) <= 0) errors.push('price must be a positive number');
    if (!sku.trim()) errors.push('sku (VIN/Chassis No.) is required — it is the unique identifier for this vehicle');
    const disc = parseFloat(discount) || 0;
    if (disc < 0 || disc > 100) errors.push('discount must be between 0 and 100');

    // ── Category ──
    const { category, error: catError } = resolveCategory(first['category_name'] || '', ctx);
    if (catError) errors.push(catError);

    // ── Brand — must match if supplied ──
    const { brand, error: brandError } = resolveBrand(first['brand_name'] || '', ctx);
    if (brandError) errors.push(brandError);

    // ── Model & Trim (optional, scoped to brand) — must match if supplied ──
    let modelId = '';
    let trimId = '';
    if (brand) {
        const { model, error: modelError } = resolveModel(first['model_name'] || '', brand.id!, ctx);
        if (modelError) errors.push(modelError);
        if (model) {
            modelId = model.id || '';
            const { trimId: resolvedTrimId, error: trimError } = resolveTrim(first['trim_name'] || '', model);
            if (trimError) errors.push(trimError);
            trimId = resolvedTrimId;
        }
    } else if (first['model_name']) {
        errors.push('model_name was supplied but brand_name was not — brand must be given to validate the model');
    }

    // ── Branch (required — one car, one branch, no quantity) ──
    const { branch, error: branchError } = resolveBranch(first['branch_name'] || '', ctx);
    if (branchError) errors.push(branchError);

    const formData: ProductFormData = {
        name: productName,
        description,
        price,
        discount,
        unitCost,
        isFeatured,
        categoryId: category?.id || '',
        subcategoryId: '',
        brandId: brand?.id || '',
        modelId,
        trimId,
        sku,
        images: [],
        branchId: branch?.id || '',
        modelYear: first['model_year'] || '',
        registrationYear: first['registration_year'] || '',
        mileageKm: first['mileage_km'] || '',
        fuelType: first['fuel_type'] || '',
        transmission: first['transmission'] || '',
        engineCapacityCc: first['engine_cc'] || '',
        driveType: first['drive_type'] || '',
        seatingCapacity: first['seating_capacity'] || '',
        exteriorColor: first['exterior_color'] || '',
        interiorColor: first['interior_color'] || '',
        condition: first['condition'] || 'Used - Good',
        status: first['status'] || 'In Stock',
        registrationNumber: first['registration_number'] || '',
        ownersCount: first['owners_count'] || '',
        isImported: false,
        importOrderId: '',
        countryOfOrigin: first['country_of_origin'] || '',
        features: [],
    };

    return {
        rowIndex: group.firstRowIndex,
        productName,
        sku,
        status: errors.length > 0 ? 'error' : 'pending',
        error: errors.join('; '),
        formData: errors.length === 0 ? formData : undefined,
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED: results table (used for both the pre-upload preview and the
// post-upload results view)
// ─────────────────────────────────────────────────────────────────────────────

const RowsTable: React.FC<{ rows: ProcessedProduct[]; variant: 'success' | 'error' | 'pending' }> = ({ rows, variant }) => (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
        <Table size="small" stickyHeader>
            <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell width={60} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Row</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Car Title</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>SKU</TableCell>
                    {variant === 'error' && (
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Why it was not added</TableCell>
                    )}
                    {variant === 'pending' && (
                        <TableCell width={110} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                    )}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((pp, idx) => (
                    <TableRow key={idx} sx={{ bgcolor: variant === 'error' ? 'error.50' : variant === 'success' ? 'success.50' : 'inherit' }}>
                        <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>#{pp.rowIndex}</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{pp.productName}</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{pp.sku || '—'}</Typography>
                        </TableCell>
                        {variant === 'error' && (
                            <TableCell sx={{ maxWidth: 420 }}>
                                <Typography variant="caption" color="error.main">{pp.error}</Typography>
                            </TableCell>
                        )}
                        {variant === 'pending' && (
                            <TableCell>
                                {pp.status === 'uploading'
                                    ? <CircularProgress size={16} />
                                    : <Chip label="Ready" size="small" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />}
                            </TableCell>
                        )}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

// Shows every reference name as a chip (e.g. valid category/brand/branch
// names for the CSV) so the user doesn't have to leave this dialog to check
// exact spelling. The dialog's content area scrolls (see DialogContent's
// overflow:auto below), so a long list just makes the dialog scroll further
// rather than needing its own truncation.
const NameChipList: React.FC<{ label: string; names: string[]; color: 'primary' | 'secondary' | 'info' | 'success' }> = ({ label, names, color }) => {
    if (names.length === 0) return null;
    return (
        <Box sx={{ mb: 1.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{label}</Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {names.map(name => (
                    <Chip key={name} label={name} size="small" color={color} variant="outlined" sx={{ fontSize: '0.7rem' }} />
                ))}
            </Stack>
        </Box>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// BULK UPLOAD MODAL
// ─────────────────────────────────────────────────────────────────────────────

interface BulkCarUploadProps {
    open: boolean;
    onClose: () => void;
    onShowToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const BulkCarUpload: React.FC<BulkCarUploadProps> = ({ open, onClose, onShowToast }) => {
    const { currentUser } = useAuth();
    const invalidateProducts = useInvalidateProducts();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Reference data
    const [categories, setCategories] = useState<Category[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [carModels, setCarModels] = useState<CarModel[]>([]);
    const [loadingRef, setLoadingRef] = useState(false);

    // Upload state
    const [processedProducts, setProcessedProducts] = useState<ProcessedProduct[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [stats, setStats] = useState<UploadStats>({ total: 0, success: 0, failed: 0 });
    const [phase, setPhase] = useState<'idle' | 'preview' | 'uploading' | 'done'>('idle');
    const [fileError, setFileError] = useState('');

    // ── Load reference data whenever the dialog opens ──
    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                setLoadingRef(true);
                const [cats, models, whs, brs] = await Promise.all([
                    CategoryService.getMainCategories(),
                    fetchActiveCarModels(),
                    WarehouseService.getActiveWarehouses(),
                    fetchActiveBrands(),
                ]);
                setCategories(cats);
                setCarModels(models);
                setWarehouses(whs);
                setBrands(brs);
            } catch (e) {
                console.error('Error loading reference data:', e);
                onShowToast('Failed to load reference data', 'error');
            } finally {
                setLoadingRef(false);
            }
        })();
    }, [open, onShowToast]);

    // ── Download sample CSV ──
    const handleDownloadSample = () => {
        const csv = generateSampleCSV(categories, brands, warehouses, carModels);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sample_products.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    // ── File selection ──
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.name.endsWith('.csv')) {
            setFileError('Please upload a .csv file');
            return;
        }
        setFileError('');
        const reader = new FileReader();
        reader.onload = ev => {
            const text = ev.target?.result as string;
            processCSV(text);
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    const processCSV = (text: string) => {
        const { headers, rows, columnCounts } = parseCSV(text);

        if (headers.length === 0 || rows.length === 0) {
            setFileError('CSV file is empty or has no data rows');
            return;
        }

        const required = ['car_title', 'category_name', 'price', 'sku', 'branch_name'];
        const missing = required.filter(h => !headers.includes(h));
        if (missing.length > 0) {
            setFileError(`Missing required columns: ${missing.join(', ')}`);
            return;
        }

        // A row with more/fewer columns than the header row almost always
        // means a text field (typically description) contains a comma but
        // wasn't wrapped in quotes, so every column after it shifted — flag
        // it as an error row instead of silently uploading misaligned data.
        const parsedRows: ParsedRow[] = rows.map((raw, idx) => {
            if (columnCounts[idx] !== headers.length) {
                raw = { ...raw, __columnMismatch: `Row has ${columnCounts[idx]} column(s), expected ${headers.length} — check for a comma inside description (or another field) that isn't wrapped in quotes` };
            }
            return { rowIndex: idx + 1, raw };
        });
        const groups = groupRows(parsedRows);

        const ctx: TransformContext = {
            categories,
            brands,
            warehouses: warehouses.filter(w => w.isActive),
            carModels,
        };

        const processed = groups.map(g => transformGroup(g, ctx));

        setProcessedProducts(processed);
        const total = processed.length;
        const failed = processed.filter(p => p.status === 'error').length;
        setStats({ total, success: 0, failed });
        setPhase('preview');
    };

    // ── Upload all valid products ──
    const handleUpload = async () => {
        const toUpload = processedProducts.filter(p => p.status === 'pending' && p.formData);
        if (toUpload.length === 0) {
            onShowToast('No valid products to upload', 'warning');
            return;
        }

        setUploading(true);
        setPhase('uploading');
        let success = 0;
        let failed = processedProducts.filter(p => p.status === 'error').length;

        const update = (idx: number, status: RowStatus, error?: string) => {
            setProcessedProducts(prev => prev.map((p, i) => i === idx ? { ...p, status, error } : p));
        };

        for (let i = 0; i < processedProducts.length; i++) {
            const pp = processedProducts[i];
            if (pp.status !== 'pending' || !pp.formData) {
                setUploadProgress(Math.round(((i + 1) / processedProducts.length) * 100));
                continue;
            }

            update(i, 'uploading');
            try {
                await ProductService.createProduct(pp.formData, currentUser?.uid || '', currentUser?.displayName || currentUser?.email || 'Unknown');
                update(i, 'success');
                success++;
            } catch (e: any) {
                update(i, 'error', e.message || 'Unknown error while saving to the database');
                failed++;
            }
            setUploadProgress(Math.round(((i + 1) / processedProducts.length) * 100));
        }

        setStats({ total: processedProducts.length, success, failed });
        setUploading(false);
        setPhase('done');

        if (success > 0) {
            invalidateProducts();
            onShowToast(`${success} product${success !== 1 ? 's' : ''} uploaded successfully`, 'success');
        }
        if (failed > 0) {
            onShowToast(`${failed} product${failed !== 1 ? 's' : ''} were not added`, 'error');
        }
    };

    const handleReset = () => {
        setProcessedProducts([]);
        setStats({ total: 0, success: 0, failed: 0 });
        setPhase('idle');
        setUploadProgress(0);
        setFileError('');
    };

    const handleClose = () => {
        if (uploading) return;
        handleReset();
        onClose();
    };

    const pendingRows = processedProducts.filter(p => p.status === 'pending' || p.status === 'uploading');
    const errorRows = processedProducts.filter(p => p.status === 'error');
    const successRows = processedProducts.filter(p => p.status === 'success');

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}
        >
            {/* ── Header ── */}
            <DialogTitle sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <UploadIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Bulk Product Upload</Typography>
                        {phase === 'preview' && (
                            <Chip label={`${processedProducts.length} products parsed`} size="small" color="primary" variant="outlined" />
                        )}
                    </Stack>
                    <IconButton onClick={handleClose} size="small" disabled={uploading}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </DialogTitle>

            {/* ── Content ── */}
            <DialogContent sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                {loadingRef ? (
                    <Box display="flex" alignItems="center" justifyContent="center" py={8}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                            Loading reference data…
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* ── Instructions ──
                            The margin lives on this wrapping Box, not the Accordion itself —
                            MUI's Accordion styles force margin-top/bottom:0 on a
                            first-of-type/last-of-type Accordion the moment it's expanded
                            (see accordionClasses.expanded rules), which was silently
                            collapsing the mt/mb here whenever the section was open. */}
                        <Box sx={{ mt: 2, mb: 2 }}>
                        <Accordion defaultExpanded={phase === 'idle'} sx={{ '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: '8px !important' }} elevation={0}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <InfoIcon color="primary" sx={{ fontSize: 18 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>CSV Format Guide & Sample Download</Typography>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={2}>
                                    <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                                        <Typography variant="body2">
                                            One row = one physical car. There is no quantity or variant column — a car is a
                                            single VIN-identified unit, not a stackable retail SKU.
                                        </Typography>
                                    </Alert>

                                    <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
                                        <Typography variant="body2">
                                            <strong>Category and Branch are required</strong>, and <strong>Brand, Model and Trim must exactly
                                            match records that already exist in the database</strong> (case-insensitive). Leave a cell blank
                                            to skip an optional field. If a name doesn't match anything in the database,
                                            that entire row will <strong>not</strong> be added — nothing partial or incorrect is ever inserted.
                                            Create any missing brand/model/trim first (Car Models page), then re-run the upload.
                                        </Typography>
                                    </Alert>

                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                            Brand, Model & Trim — optional, but must match the database if provided
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                            {['brand_name', 'model_name', 'trim_name'].map(c => (
                                                <Chip key={c} label={c} size="small" color="secondary" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                                            ))}
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                            Your available reference data
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                                            <Chip label={`${categories.length} categories`} size="small" color="primary" variant="outlined" />
                                            <Chip label={`${brands.length} brands`} size="small" color="secondary" variant="outlined" />
                                            <Chip label={`${warehouses.filter(w => w.isActive).length} active branches`} size="small" color="info" variant="outlined" />
                                            <Chip label={`${carModels.length} models`} size="small" color="success" variant="outlined" />
                                        </Stack>

                                        <NameChipList label="Categories" names={categories.map(c => c.name)} color="primary" />
                                        <NameChipList label="Brands" names={brands.map(b => b.name)} color="secondary" />
                                        <NameChipList label="Active Branches" names={warehouses.filter(w => w.isActive).map(w => w.name)} color="info" />
                                        <NameChipList label="Models" names={carModels.map(m => `${m.brandName ? m.brandName + ' ' : ''}${m.name}`)} color="success" />

                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                            Use these exact names in your CSV — check the Car Models page for the full brand/model/trim list.
                                        </Typography>
                                    </Box>

                                    <Button
                                        startIcon={<DownloadIcon />}
                                        variant="outlined"
                                        size="small"
                                        onClick={handleDownloadSample}
                                        sx={{ alignSelf: 'flex-start' }}
                                    >
                                        Download Sample CSV
                                    </Button>
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                        </Box>

                        {/* ── File Upload ── */}
                        {phase === 'idle' && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 4, borderRadius: 2, borderStyle: 'dashed', borderColor: 'primary.light',
                                    bgcolor: 'primary.50', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
                                    '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.100' },
                                }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <UploadIcon sx={{ fontSize: 48, color: 'primary.light', mb: 1 }} />
                                <Typography variant="h6" color="primary" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Click to select your CSV file
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Only .csv files are supported
                                </Typography>
                                <input ref={fileInputRef} type="file" accept=".csv" hidden onChange={handleFileChange} />
                            </Paper>
                        )}

                        {fileError && (
                            <Alert severity="error" sx={{ mt: 2, borderRadius: 1.5 }}>{fileError}</Alert>
                        )}

                        {/* ── Preview (before upload) ── */}
                        {phase === 'preview' && (
                            <>
                                <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
                                    <Chip label={`${processedProducts.length} rows parsed`} size="small" color="default" variant="outlined" />
                                    <Chip icon={<CheckCircleIcon />} label={`${pendingRows.length} ready to upload`} size="small" color="success" variant="outlined" />
                                    <Chip icon={<ErrorIcon />} label={`${errorRows.length} will not be added`} size="small" color="error" variant="outlined" />
                                </Stack>

                                {pendingRows.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'success.main' }}>
                                            ✓ Ready to upload ({pendingRows.length})
                                        </Typography>
                                        <RowsTable rows={pendingRows} variant="pending" />
                                    </Box>
                                )}

                                {errorRows.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'error.main' }}>
                                            ✗ Will not be added ({errorRows.length})
                                        </Typography>
                                        <RowsTable rows={errorRows} variant="error" />
                                    </Box>
                                )}
                            </>
                        )}

                        {/* ── Uploading ── */}
                        {phase === 'uploading' && (
                            <Box sx={{ mb: 2 }}>
                                <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 6, borderRadius: 3, mb: 1 }} />
                                <Typography variant="caption" color="text.secondary">Uploading… {uploadProgress}%</Typography>
                            </Box>
                        )}

                        {/* ── Results (after upload) ── */}
                        {phase === 'done' && (
                            <>
                                <Alert
                                    severity={stats.failed > 0 && stats.success === 0 ? 'error' : stats.failed > 0 ? 'warning' : 'success'}
                                    sx={{ mb: 3, borderRadius: 1.5 }}
                                >
                                    <Typography variant="body2">
                                        Upload complete — <strong>{stats.success} succeeded</strong>, {stats.failed} not added.
                                    </Typography>
                                </Alert>

                                {successRows.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'success.main' }}>
                                            ✓ Successfully Added ({successRows.length})
                                        </Typography>
                                        <RowsTable rows={successRows} variant="success" />
                                    </Box>
                                )}

                                {errorRows.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Divider sx={{ mb: 2 }} />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'error.main' }}>
                                            ✗ Failed — Not Added ({errorRows.length})
                                        </Typography>
                                        <RowsTable rows={errorRows} variant="error" />
                                    </Box>
                                )}
                            </>
                        )}
                    </>
                )}
            </DialogContent>

            {/* ── Footer ── */}
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
                {phase === 'idle' && (
                    <>
                        <Button onClick={handleClose} variant="outlined" size="small">Cancel</Button>
                        <Button
                            component="label"
                            variant="contained"
                            size="small"
                            startIcon={<UploadIcon />}
                            disabled={loadingRef}
                        >
                            Select CSV File
                            <input type="file" hidden accept=".csv" onChange={handleFileChange} />
                        </Button>
                    </>
                )}

                {phase === 'preview' && (
                    <>
                        <Button onClick={handleReset} variant="outlined" size="small" startIcon={<RefreshIcon />}>
                            Reset
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        {errorRows.length > 0 && (
                            <Typography variant="caption" color="error.main" sx={{ alignSelf: 'center' }}>
                                {errorRows.length} row{errorRows.length !== 1 ? 's' : ''} will not be added
                            </Typography>
                        )}
                        <Button
                            onClick={handleUpload}
                            variant="contained"
                            size="small"
                            startIcon={<UploadIcon />}
                            disabled={pendingRows.length === 0}
                        >
                            Upload {pendingRows.length} Product{pendingRows.length !== 1 ? 's' : ''}
                        </Button>
                    </>
                )}

                {phase === 'uploading' && (
                    <Button variant="outlined" size="small" disabled>
                        Uploading…
                    </Button>
                )}

                {phase === 'done' && (
                    <>
                        <Button onClick={handleReset} variant="outlined" size="small" startIcon={<RefreshIcon />}>
                            Upload Another
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Button onClick={handleClose} variant="contained" size="small">
                            Close
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default BulkCarUpload;
